import { motion } from "framer-motion";
import { Mail, MapPin, Phone, Send } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import Seo from "../components/Seo";
import { PAGES } from "../seo/meta.mjs";

const GOOGLE_SCRIPT_URL =
"https://script.google.com/macros/s/AKfycbzVk8VEduakZDE_b77CAlDioVahyabUKt-7ateZ-1SrqwK3JWXFx1fzLKMA1bRp3UP7/exec"; // replace with your Apps Script URL

const Contact = () => {

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    message: ""
  });

  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {

    e.preventDefault();

    setSubmitting(true);

    try {

      await fetch(GOOGLE_SCRIPT_URL, {
        method: "POST",
        body: JSON.stringify({
          ...formData,
          source: "Website Contact Form"
        })
      });

      toast.success("Thank you! We will get back to you soon.");

      setFormData({
        name: "",
        email: "",
        phone: "",
        company: "",
        message: ""
      });

    } catch (error) {

      console.error("Error submitting form:", error);

      toast.error("Failed to submit form. Please try again.");

    } finally {

      setSubmitting(false);

    }

  };

  return (

    <div className="min-h-screen pt-20">
      <Seo {...PAGES.contact} />

      {/* HERO */}

      <section className="py-24 bg-slate-950">

        <div className="max-w-7xl mx-auto px-6 md:px-12">

          <motion.h1
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8 }}
            className="font-heading font-light text-5xl md:text-7xl tracking-tighter text-white mb-6 text-center"
          >
            Get In <span className="text-ocean-500">Touch</span>
          </motion.h1>

          <motion.p
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-lg md:text-xl text-slate-300 max-w-3xl mx-auto text-center"
          >
            Let's discuss your hydro power project requirements
          </motion.p>

        </div>

      </section>


      {/* CONTACT SECTION */}

      <section className="py-24 bg-slate-900">

        <div className="max-w-7xl mx-auto px-6 md:px-12">

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">

            {/* FORM */}

            <div>

              <h2 className="font-heading text-3xl font-medium text-white mb-8">
                Discuss Your Project
              </h2>

              <form onSubmit={handleSubmit} className="space-y-6">

                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder="Your Name"
                  className="w-full bg-slate-100 border border-slate-300 focus:border-ocean-500 h-12 px-4 text-slate-900 placeholder:text-slate-500 outline-none"
                />

                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="Email Address"
                  className="w-full bg-slate-100 border border-slate-300 focus:border-ocean-500 h-12 px-4 text-slate-900 placeholder:text-slate-500 outline-none"
                />

                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  placeholder="Phone Number"
                  className="w-full bg-slate-100 border border-slate-300 focus:border-ocean-500 h-12 px-4 text-slate-900 placeholder:text-slate-500 outline-none"
                />

                <input
                  type="text"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  required
                  placeholder="Company Name"
                  className="w-full bg-slate-100 border border-slate-300 focus:border-ocean-500 h-12 px-4 text-slate-900 placeholder:text-slate-500 outline-none"
                />

                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows="5"
                  placeholder="Project Details"
                  className="w-full bg-slate-100 border border-slate-300 focus:border-ocean-500 p-4 text-slate-900 placeholder:text-slate-500 outline-none resize-none"
                />

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full px-8 py-4 bg-ocean-500 hover:bg-ocean-600 text-white font-medium uppercase tracking-widest text-xs transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {submitting ? "Sending..." : "Send Message"}
                  <Send size={16} />
                </button>

              </form>

            </div>


            {/* CONTACT INFO */}

            <div className="space-y-8">

              <h2 className="font-heading text-3xl font-medium text-white mb-8">
                Contact Information
              </h2>

              <div className="space-y-6">

                <div className="flex items-start gap-4">

                  <div className="w-12 h-12 border border-white/10 flex items-center justify-center">
                    <MapPin size={20} className="text-ocean-500" />
                  </div>

                  <div>
                    <p className="text-slate-400">Plot No.6 Kh No. 1044</p>
                    <p className="text-slate-400">Twin Industrial Estate</p>
                    <p className="text-slate-400">Central Hope Town, Selaqui</p>
                    <p className="text-slate-400">Dehradun, India</p>
                  </div>

                </div>

                <div className="flex items-start gap-4">

                  <div className="w-12 h-12 border border-white/10 flex items-center justify-center">
                    <Phone size={20} className="text-ocean-500" />
                  </div>

                  <div>
                    <p className="text-slate-400">+91 9412075715</p>
                    <p className="text-slate-400">+91 6396557404</p>
                  </div>

                </div>

                <div className="flex items-start gap-4">

                  <div className="w-12 h-12 border border-white/10 flex items-center justify-center">
                    <Mail size={20} className="text-ocean-500" />
                  </div>

                  <div>
                    <p className="text-slate-400">info@ytekcontrols.com</p>
                    <p className="text-slate-400">sales@ytekcontrols.com</p>
                  </div>

                </div>

              </div>

            </div>

          </div>

        </div>

      </section>


      {/* MAP */}

      <section className="h-96 bg-slate-950">

        <iframe
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1337.8810229847597!2d77.83779005069103!3d30.356378152983737!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x39092bba402abcfb%3A0x59aec51d1ea2ba56!2sYtek%20Controls!5e0!3m2!1sen!2sin!4v1770558593790!5m2!1sen!2sin"
          width="100%"
          height="100%"
          style={{ border: 0 }}
          loading="lazy"
          title="Office Location"
        />

      </section>

    </div>

  );

};

export default Contact;